<script lang="ts">
    import { createEventDispatcher } from 'svelte';
    
    export let isOpen: boolean = false;
    export let type: 'success' | 'error' | 'info' = 'success';
    export let title: string = '';
    export let message: string = '';
    export let copyUrl: string = '';
  
    const dispatch = createEventDispatcher();
    let isCopied = false; // New state to track if copy was successful
  
    const close = () => {
      isCopied = false; // Reset state
      dispatch('close');
    };
  
    const handleCopy = async () => {
      let success = false;
  
      // 1. Try Modern API
      if (navigator.clipboard && window.isSecureContext) {
          try {
              await navigator.clipboard.writeText(copyUrl);
              success = true;
          } catch (err) {
              console.warn("Clipboard API failed, trying fallback...");
          }
      }
  
      // 2. Fallback (if Modern API failed)
      if (!success) {
          try {
              const textArea = document.createElement("textarea");
              textArea.value = copyUrl;
              
              // A. Hide element visually so it doesn't flash
              textArea.style.position = "fixed";
              textArea.style.left = "-9999px";
              textArea.style.top = "0";
              textArea.style.opacity = "0";
              
              // B. Prevent Keyboard: 'readonly' stops the keyboard from popping up
              textArea.setAttribute('readonly', '');
              
              document.body.appendChild(textArea);
              
              textArea.select();
              textArea.setSelectionRange(0, 99999); // iOS specific fix
              
              document.execCommand('copy');
              document.body.removeChild(textArea);
              success = true;
          } catch (err) {
              console.error("Fallback failed", err);
          }
      }
  
      if (success) {
          // 3. UI Feedback: Change button text instead of using alert()
          isCopied = true;
          
          // Close modal automatically after 1 second
          setTimeout(() => {
              close();
          }, 1000);
      }
    };
  </script>
  
  {#if isOpen}
    <div class="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div 
          class="absolute inset-0 bg-black bg-opacity-40 transition-opacity"
          on:click={close}
      ></div>
  
      <div class="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden transform transition-all relative z-10">
        
        <div class="p-6 text-center">
          <div class="mx-auto flex items-center justify-center h-16 w-16 rounded-full mb-4 
              {type === 'success' || isCopied ? 'bg-green-100' : type === 'error' ? 'bg-red-100' : 'bg-blue-100'}">
            
            {#if isCopied}
              <svg class="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                 <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
               </svg>
            {:else if type === 'success'}
              <svg class="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
            {:else if type === 'error'}
              <svg class="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            {:else}
              <svg class="h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
            {/if}
          </div>
  
          <h3 class="text-lg leading-6 font-bold text-gray-900 mb-2">
            {isCopied ? "ຄັດລອກລິ້ງແລ້ວ!" : title}
          </h3>
  
          <p class="text-sm text-gray-500 mb-4">
            {isCopied ? "ລິ້ງເອກະສານໄດ້ຖືກຄັດລອກໃສ່ຄລິບບອດແລ້ວ." : message}
          </p>
  
          <!-- {#if copyUrl && !isCopied}
              <div class="relative rounded-md shadow-sm">
                  <input 
                      type="text" 
                      readonly 
                      value={copyUrl} 
                      class="block w-full border-gray-300 rounded-md bg-gray-50 text-xs text-gray-500 p-2 focus:ring-blue-500 focus:border-blue-500 text-center"
                      on:click={(e) => e.currentTarget.select()}
                  />
              </div>
          {/if} -->
        </div>
  
        <div class="bg-gray-50 px-4 py-3 sm:px-6 flex justify-center gap-3">
          
          {#if copyUrl && !isCopied}
              <button
                type="button"
                class="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:text-sm"
                on:click={close}
              >
                ຍົກເລີກ
              </button>
              <button
                type="button"
                class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none sm:text-sm"
                on:click={handleCopy}
              >
                ຄັດລອກລິ້ງ
              </button>
          {:else}
              <button
                type="button"
                class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none sm:text-sm {type === 'success' || isCopied ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}"
                on:click={close}
              >
                {isCopied ? 'ຕົກລົງ' : 'ປິດ'}
              </button>
          {/if}
        </div>
      </div>
    </div>
  {/if}